import React, { useCallback, useMemo } from "react";
import { FlatList } from "react-native";
import { graphql, useLazyLoadQuery } from "react-relay";
import { WidgetIntegrationPushAppy, WidgetNote } from "widgets";
import { LazyQueryNetworkBoundrary } from "../../../../../../components/mobile/QueryNetworkBoundrary";
import { getWidgetAddWidgetSizeWithinBounds } from "../../utils/useWidgetAddWidgetSizeState";
import { WACategoryScreenProps } from "../../WANavigation";
import { WACategoryWidgetContainer } from "../../WACategoryWidgetContainer";
import { WACategoryIntegrationsQuery } from "./__generated__/WACategoryIntegrationsQuery.graphql";
import { overviewWidgetSizes } from "../../utils/widgetSizes/overviewWidgetSizes";
import { useIntlTokens } from "../../../../../../components/mobile/useIntlTokens";

import { useWACategoryLayout } from "../../utils/useWACategoryLayout";
import { WidgetEmployeeSchedule } from "widgets/src/WidgetEmployeeSchedule/WidgetEmployeeSchedule";
import { emaginWidgetSizes } from "../../utils/widgetSizes/emaginWidgetSizes";
import { ListItem } from "arkh";
import { noteWidgetSizes } from "../../utils/widgetSizes/noteWidgetSizes";
import { WidgetIntegrationTurnpike } from "widgets/src/WidgetIntegrationTurnpike/WidgetIntegrationTurnpike";
import { notEmpty } from "../../../../../../utils";
import { turnpikeWidgetSizes } from "../../utils/widgetSizes/turnpikeWidgetSizes";
import { WidgetIntegrationFlourishPraise } from "widgets/src/WidgetIntegrationFlourishPraise/WidgetIntegrationFlourishPraise";
import { WidgetKPI } from "widgets/src/WidgetKPI";
import { kpiWidgetSizes } from "../../utils/widgetSizes/kpiWidgetSizes";
import { newsfeedWidgetSizes } from "../../utils/widgetSizes/newsfeedWidgetSizes";
import { WidgetNewsfeed } from "widgets/src/WidgetNewsfeed";
import { WidgetTodoOverviewPreview } from "widgets/src/WidgetTodoOverview/WidgetTodoOverview";
import { listWidgetSizes } from "../../utils/widgetSizes/listWidgetSizes";
import { WidgetList } from "widgets/src/WidgetList";
import { WidgetTable } from "widgets/src/WidgetTable";
import { WidgetEmployeeLeave } from "widgets/src/WidgetEmployeeLeave/WidgetEmployeeLeave";
import { FormattedMessage } from "react-intl";

type Props = WACategoryScreenProps<"WACategoryIntegrations">;

export function WACategoryIntegrations(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fetchKey) => (
        <InnerWACategoryIntegrations {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWACategoryIntegrations(props: Props & { fetchKey: string }) {
  const sizes = useMemo(() => {
    const maxSize = props.route.params.maxSize;
    return {
      note: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: noteWidgetSizes,
      }),
      turnpike: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: turnpikeWidgetSizes,
      }),
      emagin: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: emaginWidgetSizes,
      }),
      employeeSchedule: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: overviewWidgetSizes,
      }),
      employeeLeave: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: overviewWidgetSizes,
      }),
      pushAppy: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: overviewWidgetSizes,
      }),
      flourishPraise: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: overviewWidgetSizes,
      }),
      kpi: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: kpiWidgetSizes,
      }),
      newsfeed: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: newsfeedWidgetSizes,
      }),
      list: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: listWidgetSizes,
      }),
      table: getWidgetAddWidgetSizeWithinBounds({
        maxSize,
        widgetSizes: listWidgetSizes,
      }),
    };
  }, [props.route.params.maxSize]);

  const data = useLazyLoadQuery<WACategoryIntegrationsQuery>(
    graphql`
      query WACategoryIntegrationsQuery(
        $noteInput: PreviewWidgetNoteIntegrationInput!
        $turnpikeInput: PreviewWidgetTurnpikeIntegrationInput!
        $storecastId: ID!
        $first: Int
        $after: String
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetEmployeeSchedule(input: {}) {
            __typename
            ...WidgetEmployeeSchedule_widgetEmployeeSchedule
          }
          previewWidgetEmployeeLeave(input: {}) {
            __typename
            ...WidgetEmployeeLeave_widgetEmployeeLeave
          }
          previewWidgetNoteIntegration(input: $noteInput) {
            __typename
            id
            title
            integrationId
            ...WidgetNote_widgetNote
          }

          previewWidgetPushAppyIntegration(
            input: { storecastId: $storecastId }
          ) {
            __typename
            ...WidgetIntegrationPushAppy_widgetIntegrationPushAppy
          }
          previewWidgetTurnpikeIntegration(input: $turnpikeInput) {
            __typename
            ...WidgetIntegrationTurnpike_widgetIntegrationTurnpike
          }
          previewWidgetFlourishPraiseIntegration(
            input: { storecastId: $storecastId }
          ) {
            __typename
            ...WidgetIntegrationFlourishPraise_widgetIntegrationFlourishPraise
          }
          previewWidgetKPIs(input: { storecastId: $storecastId }) {
            __typename
            title
            dataSource
            ...WidgetKPI_widgetKPI
          }
          previewWidgetNewsfeeds(input: { storecastId: $storecastId }) {
            __typename
            title
            newsfeedId
            ...WidgetNewsfeed_widgetNewsfeed
          }
          previewWidgetTodoOverview(
            input: { storecastId: $storecastId }
            integrations: true
          ) {
            widgets {
              __typename
              title
              category {
                id
              }
              ...WidgetTodoOverviewPreview_widget
            }
          }
          previewWidgetLists(input: { storecastId: $storecastId }) {
            __typename
            title
            dataSource
            ...WidgetList_widgetList
          }
          previewWidgetTables(input: { storecastId: $storecastId }) {
            __typename
            title
            dataSource
            ...WidgetTable_table
          }
        }
      }
    `,
    {
      storecastId: props.route.params.storecastId,
      noteInput: {
        size: sizes.note[0].h === 2 ? "TWO_X_TWO" : "TWO_X_ONE",
        storecastId: props.route.params.storecastId,
      },
      turnpikeInput: {
        size: sizes.turnpike[0].h === 2 ? "TWO_X_TWO" : "TWO_X_ONE",
        storecastId: props.route.params.storecastId,
      },
    },
    {
      fetchKey: props.fetchKey,
      fetchPolicy: "store-and-network",
    }
  );

  const onAdd = useCallback(
    (
      key:
        | "WidgetAddPushAppy"
        | "WidgetAddEmployeeSchedule"
        | "WidgetAddEmployeeLeave"
        | "WidgetAddIntegrationTurnpike"
        | "WidgetAddIntegrationFlourishPraise"
    ) => {
      props.navigation.navigateDeprecated(key, {
        defaultTeams: props.route.params.defaultTeams,
        position: props.route.params.position,
        storecastId: props.route.params.storecastId,
        unitSize: props.route.params.unitSize,
        maxSize: props.route.params.maxSize,
      });
    },
    [
      props.navigation,
      props.route.params.defaultTeams,
      props.route.params.maxSize,
      props.route.params.position,
      props.route.params.storecastId,
      props.route.params.unitSize,
    ]
  );
  const onAddFlourishPraise = useCallback(
    () => onAdd("WidgetAddIntegrationFlourishPraise"),
    [onAdd]
  );
  const onAddTurnpike = useCallback(
    () => onAdd("WidgetAddIntegrationTurnpike"),
    [onAdd]
  );
  const onAddPushAppy = useCallback(() => onAdd("WidgetAddPushAppy"), [onAdd]);
  const onAddEmployeeSchedule = useCallback(
    () => onAdd("WidgetAddEmployeeSchedule"),
    [onAdd]
  );
  const onAddEmployeeLeave = useCallback(
    () => onAdd("WidgetAddEmployeeLeave"),
    [onAdd]
  );
  const onAddNoteIntegration = useCallback(
    (integrationId: string, widgetId: string) =>
      props.navigation.navigateDeprecated("WidgetAddNoteIntegration", {
        defaultTeams: props.route.params.defaultTeams,
        position: props.route.params.position,
        storecastId: props.route.params.storecastId,
        unitSize: props.route.params.unitSize,
        maxSize: props.route.params.maxSize,
        integrationId: integrationId,
        widgetId,
      }),
    [
      props.navigation,
      props.route.params.defaultTeams,
      props.route.params.maxSize,
      props.route.params.position,
      props.route.params.storecastId,
      props.route.params.unitSize,
    ]
  );
  const onAddKPI = useCallback(
    (dataSource: string) =>
      props.navigation.navigateDeprecated("WidgetAddIntegrationKPI", {
        defaultTeams: props.route.params.defaultTeams,
        position: props.route.params.position,
        storecastId: props.route.params.storecastId,
        unitSize: props.route.params.unitSize,
        maxSize: props.route.params.maxSize,
        dataSource,
      }),
    [
      props.navigation,
      props.route.params.defaultTeams,
      props.route.params.position,
      props.route.params.storecastId,
      props.route.params.unitSize,
      props.route.params.maxSize,
    ]
  );
  const onAddNewsfeed = useCallback(
    (newsfeedId: string) =>
      props.navigation.navigateDeprecated("WidgetAddIntegrationNewsfeed", {
        defaultTeams: props.route.params.defaultTeams,
        position: props.route.params.position,
        storecastId: props.route.params.storecastId,
        unitSize: props.route.params.unitSize,
        maxSize: props.route.params.maxSize,
        newsfeedId,
      }),
    [
      props.navigation,
      props.route.params.defaultTeams,
      props.route.params.position,
      props.route.params.storecastId,
      props.route.params.unitSize,
      props.route.params.maxSize,
    ]
  );
  const onAddTodo = useCallback(
    (categoryId: string) =>
      props.navigation.navigateDeprecated("WidgetAddTodoCategory", {
        defaultTeams: props.route.params.defaultTeams,
        position: props.route.params.position,
        storecastId: props.route.params.storecastId,
        unitSize: props.route.params.unitSize,
        maxSize: props.route.params.maxSize,
        categoryId,
      }),
    [
      props.navigation,
      props.route.params.defaultTeams,
      props.route.params.position,
      props.route.params.storecastId,
      props.route.params.unitSize,
      props.route.params.maxSize,
    ]
  );
  const onAddList = useCallback(
    (dataSource: string) =>
      props.navigation.navigateDeprecated("WidgetAddIntegrationList", {
        defaultTeams: props.route.params.defaultTeams,
        position: props.route.params.position,
        storecastId: props.route.params.storecastId,
        unitSize: props.route.params.unitSize,
        maxSize: props.route.params.maxSize,
        dataSource,
      }),
    [
      props.navigation,
      props.route.params.defaultTeams,
      props.route.params.position,
      props.route.params.storecastId,
      props.route.params.unitSize,
      props.route.params.maxSize,
    ]
  );
  const onAddTable = useCallback(
    (dataSource: string) =>
      props.navigation.navigateDeprecated("WidgetAddIntegrationTable", {
        defaultTeams: props.route.params.defaultTeams,
        position: props.route.params.position,
        storecastId: props.route.params.storecastId,
        unitSize: props.route.params.unitSize,
        maxSize: props.route.params.maxSize,
        dataSource,
      }),
    [
      props.navigation,
      props.route.params.defaultTeams,
      props.route.params.position,
      props.route.params.storecastId,
      props.route.params.unitSize,
      props.route.params.maxSize,
    ]
  );

  const intl_tokens = useIntlTokens();
  const contentContainerStyle = useWACategoryLayout();

  return (
    <FlatList
      style={{ flex: 1 }}
      contentContainerStyle={contentContainerStyle}
      data={[
        data.dashboard.previewWidgetTurnpikeIntegration,
        data.dashboard.previewWidgetEmployeeSchedule,
        data.dashboard.previewWidgetEmployeeLeave,
        data.dashboard.previewWidgetPushAppyIntegration,
        data.dashboard.previewWidgetFlourishPraiseIntegration,
        ...data.dashboard.previewWidgetKPIs,
        ...data.dashboard.previewWidgetNoteIntegration,
        ...data.dashboard.previewWidgetNewsfeeds,
        ...data.dashboard.previewWidgetTodoOverview.widgets,
        ...data.dashboard.previewWidgetLists,
        ...data.dashboard.previewWidgetTables,
      ].filter(notEmpty)}
      renderItem={({ item, index }) => {
        switch (item.__typename) {
          case "WidgetIntegrationTurnpike":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.turnpike[1]}
                title="Turnpike"
                size={sizes.turnpike[0]}
                unitSize={props.route.params.unitSize}
                onAdd={onAddTurnpike}
              >
                <WidgetIntegrationTurnpike
                  mode="IDLE"
                  widget={item}
                  storecastId={props.route.params.storecastId}
                  interactive={false}
                />
              </WACategoryWidgetContainer>
            );
          case "WidgetEmployeeLeave":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.employeeLeave[1]}
                title={intl_tokens.Absence}
                size={sizes.employeeLeave[0]}
                unitSize={props.route.params.unitSize}
                onAdd={onAddEmployeeLeave}
              >
                <WidgetEmployeeLeave
                  widget={item}
                  mode="IDLE"
                  interactive={false}
                  storecastId={props.route.params.storecastId}
                />
              </WACategoryWidgetContainer>
            );
          case "WidgetEmployeeSchedule":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.employeeSchedule[1]}
                title="Schema"
                size={sizes.employeeSchedule[0]}
                unitSize={props.route.params.unitSize}
                onAdd={onAddEmployeeSchedule}
              >
                <WidgetEmployeeSchedule
                  widget={item}
                  mode="IDLE"
                  interactive={false}
                  storecastId={props.route.params.storecastId}
                />
              </WACategoryWidgetContainer>
            );
          case "WidgetIntegrationFlourishPraise":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.flourishPraise[1]}
                title="Flourish Praise"
                size={sizes.flourishPraise[0]}
                unitSize={props.route.params.unitSize}
                onAdd={onAddFlourishPraise}
              >
                <WidgetIntegrationFlourishPraise
                  mode="IDLE"
                  widget={item}
                  storecastId={props.route.params.storecastId}
                  interactive={false}
                />
              </WACategoryWidgetContainer>
            );
          case "WidgetIntegrationPushAppy":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.pushAppy[1]}
                title="PushAppy"
                size={sizes.pushAppy[0]}
                unitSize={props.route.params.unitSize}
                onAdd={onAddPushAppy}
              >
                <WidgetIntegrationPushAppy
                  mode="IDLE"
                  widget={item}
                  storecastId={props.route.params.storecastId}
                  interactive={false}
                />
              </WACategoryWidgetContainer>
            );
          case "WidgetNote": {
            return (
              <WACategoryWidgetContainer
                key={item.id}
                hasSizeWithinBounds={sizes.note[1]}
                title={item.title}
                size={sizes.note[0]}
                unitSize={props.route.params.unitSize}
                onAdd={() => {
                  if (item.integrationId === null) {
                    throw new Error("integrationId is null");
                  }
                  onAddNoteIntegration(item.integrationId, item.id);
                }}
              >
                <WidgetNote
                  mode="IDLE"
                  interactive={false}
                  idiom="STATIC"
                  widget={item}
                  fetchKey={props.fetchKey}
                  storecastId={props.route.params.storecastId}
                />
              </WACategoryWidgetContainer>
            );
          }
          case "WidgetKPI":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.kpi[1]}
                title={item.title}
                size={sizes.kpi[0]}
                unitSize={props.route.params.unitSize}
                onAdd={() => onAddKPI(item.dataSource)}
              >
                <WidgetKPI
                  mode="IDLE"
                  interactive={false}
                  idiom="STATIC"
                  widget={item}
                />
              </WACategoryWidgetContainer>
            );
          case "WidgetNewsfeed":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.kpi[1]}
                title={item.title}
                size={sizes.kpi[0]}
                unitSize={props.route.params.unitSize}
                onAdd={() => onAddNewsfeed(item.newsfeedId)}
              >
                <WidgetNewsfeed
                  mode="IDLE"
                  interactive={false}
                  idiom="STATIC"
                  widget={item}
                />
              </WACategoryWidgetContainer>
            );
          case "WidgetTodoOverview":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.kpi[1]}
                title={item.title}
                size={sizes.kpi[0]}
                unitSize={props.route.params.unitSize}
                onAdd={() => onAddTodo(item.category!.id)}
              >
                <WidgetTodoOverviewPreview
                  mode="IDLE"
                  interactive={false}
                  idiom="STATIC"
                  widget={item}
                />
              </WACategoryWidgetContainer>
            );
          case "WidgetList":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.list[1]}
                title={item.title}
                size={sizes.list[0]}
                unitSize={props.route.params.unitSize}
                onAdd={() => onAddList(item.dataSource)}
              >
                <WidgetList
                  mode="IDLE"
                  interactive={false}
                  idiom="STATIC"
                  widget={item}
                />
              </WACategoryWidgetContainer>
            );
          case "WidgetTable":
            return (
              <WACategoryWidgetContainer
                hasSizeWithinBounds={sizes.table[1]}
                title={item.title}
                size={sizes.table[0]}
                unitSize={props.route.params.unitSize}
                onAdd={() => onAddTable(item.dataSource)}
              >
                <WidgetTable
                  mode="IDLE"
                  interactive={false}
                  idiom="STATIC"
                  widget={item}
                />
              </WACategoryWidgetContainer>
            );
          default:
            return null;
        }
      }}
      ListFooterComponent={
        <>
          <ListItem
            onPress={() => {
              props.navigation.navigate("StoreAdmin", {
                screen: "IntegrationAdmin",
              });
            }}
            isFree
            border="border-primary"
            right={<ListItem.NavArrow />}
          >
            <ListItem.Title>
              <FormattedMessage
                defaultMessage="Administrate Integrations"
                description="Administrate Integrations"
              />
            </ListItem.Title>
          </ListItem>
        </>
      }
    />
  );
}
